import { AGENT_API_BASE_URL } from "./agentConfig";

export const BACKEND_ROOT = AGENT_API_BASE_URL.replace(/\/api\/website\/?$/, "");

export const MEDIA_CDN_BASE = (
  process.env.REACT_APP_MEDIA_BASE_URL || ""
).replace(/\/$/, "");

const PRODUCTION_MEDIA_HOSTS = ["https://api.way2umrah.com", "https://be.way2umrah.com"];

const FOLDER_ALIASES = {
  umrah_package: ["umrah_package", "umrah_packages"],
  umrah_packages: ["umrah_packages", "umrah_package"],
  umrah_hotel_list: ["umrah_hotel_list", "umrah-hotels", "umrah_hotels"],
  umrah_hotel_gallery: ["umrah_hotel_gallery", "umrah_hotel_list_gallery"],
};

export const CATALOG_PAGE_SIZE = 12;

export function unwrapData(payload) {
  return payload?.data ?? payload ?? {};
}

export function asArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.hotels)) return value.hotels;
  if (Array.isArray(value?.packages)) return value.packages;
  if (Array.isArray(value?.bookings)) return value.bookings;
  if (Array.isArray(value?.customers)) return value.customers;
  if (Array.isArray(value?.transactions)) return value.transactions;
  if (Array.isArray(value?.commissions)) return value.commissions;
  if (Array.isArray(value?.documents)) return value.documents;
  if (Array.isArray(value?.pilgrims)) return value.pilgrims;
  if (Array.isArray(value?.notifications)) return value.notifications;
  if (Array.isArray(value?.tickets)) return value.tickets;
  if (Array.isArray(value?.requests)) return value.requests;
  return [];
}

export function readPagination(payload) {
  const data = unwrapData(payload);
  const pagination = data?.pagination || payload?.meta?.pagination || payload?.pagination || {};
  const page = Number(pagination.page || pagination.current_page || pagination.currentPage || data?.page || payload?.page) || 1;
  const limit = Number(pagination.limit || pagination.per_page || pagination.itemsPerPage || data?.limit || payload?.limit) || 0;
  const total = Number(pagination.total || pagination.totalItems || data?.total || payload?.total) || 0;
  const totalPages = Number(pagination.totalPages || pagination.total_pages || data?.totalPages) || 0;
  const hasNext =
    pagination.hasNext != null
      ? Boolean(pagination.hasNext)
      : pagination.has_next != null
        ? Boolean(pagination.has_next)
        : totalPages > 0
          ? page < totalPages
          : total > 0 && limit > 0
            ? page * limit < total
            : false;

  return { page, limit, total, hasNext };
}

export function appendUniqueById(current, next, getId) {
  const seen = new Set(current.map(getId).filter((id) => id != null && id !== ""));
  return [
    ...current,
    ...next.filter((item) => {
      const id = getId(item);
      if (id == null || id === "" || seen.has(id)) return false;
      seen.add(id);
      return true;
    }),
  ];
}

export function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || "download";
  anchor.click();
  window.URL.revokeObjectURL(url);
}

export function firstRecord(value) {
  if (Array.isArray(value)) return value[0] || null;
  return value || null;
}

function encodeMediaPath(fileName) {
  return String(fileName)
    .replace(/\\/g, "/")
    .split("/")
    .filter(Boolean)
    .map((part) => {
      try {
        if (part !== decodeURIComponent(part)) {
          return part;
        }
      } catch (error) {
        // Keep the original segment if it is not valid URI encoding.
      }
      return encodeURIComponent(part);
    })
    .join("/");
}

function fileNameFromValue(fileName) {
  const value = String(fileName || "").trim();
  if (!value) return "";

  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value);
      const path = parsed.pathname.replace(/^\/+/, "").replace(/^(uploads|upload)\//i, "");
      return path || parsed.pathname.split("/").filter(Boolean).pop() || "";
    } catch (error) {
      return value.split(/[\\/]/).filter(Boolean).pop() || "";
    }
  }

  return value.replace(/\\/g, "/").replace(/^\/+/, "").replace(/^(uploads|upload)\//i, "");
}

export function mediaCandidates(folder, fileName) {
  if (!fileName) return [];

  const rawValue = String(fileName).trim();
  let extractedName = fileNameFromValue(rawValue);
  if (!extractedName) return [];

  const folderAliases = FOLDER_ALIASES[folder] || [folder];
  for (const alias of folderAliases) {
    const prefix = `${alias}/`;
    if (extractedName.toLowerCase().startsWith(prefix.toLowerCase())) {
      extractedName = extractedName.slice(prefix.length);
      break;
    }
  }

  const encoded = encodeMediaPath(extractedName);
  const folders = folderAliases.map(encodeMediaPath);
  const hosts = [BACKEND_ROOT, MEDIA_CDN_BASE, ...PRODUCTION_MEDIA_HOSTS].filter(Boolean);
  const prefixes = ["uploads", "upload"];
  const urls = [];

  hosts.forEach((host) => {
    prefixes.forEach((prefix) => {
      folders.forEach((folderPath) => {
        urls.push(`${host}/${prefix}/${folderPath}/${encoded}`);
      });
    });
  });

  prefixes.forEach((prefix) => {
    folders.forEach((folderPath) => {
      urls.push(`/${prefix}/${folderPath}/${encoded}`);
    });
  });

  if (/^https?:\/\//i.test(rawValue)) {
    urls.push(rawValue);
  }

  return [...new Set(urls)];
}

export function mediaUrl(folder, fileName) {
  return mediaCandidates(folder, fileName)[0] || "";
}

export function formatCurrency(value, currency = "INR") {
  const amount = Number(String(value || 0).replace(/,/g, ""));
  if (Number.isNaN(amount)) return `${currency} 0`;
  return `${currency} ${amount.toLocaleString("en-IN")}`;
}

export function packageNetPrice(pkg) {
  return pkg?.netPrice ?? pkg?.shareRates?.twin ?? pkg?.perAdultTwin ?? pkg?.approxPrice ?? 0;
}

export function packageRsp(pkg) {
  return pkg?.shareRspRates?.twin ?? pkg?.perAdultTwinRsp ?? pkg?.rsp ?? pkg?.approxPrice ?? 0;
}

export function packageBookingTotal(pkg, passengerCount = 1) {
  const unit = Number(packageNetPrice(pkg)) || 0;
  const pax = Number.parseInt(passengerCount, 10);
  const count = Number.isFinite(pax) && pax > 0 ? pax : 0;
  return unit * count;
}

export function formatDate(value) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatDateTime(value) {
  if (!value) return "Not set";
  const raw = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}/.test(raw) && /[ap]m/i.test(raw)) {
    return raw;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function initialsFromName(name, fallback = "WU") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export async function fetchBySlugOrId(getFn, identifier, slugPath, idPath) {
  const encoded = encodeURIComponent(identifier);
  const isNumeric = /^\d+$/.test(String(identifier));
  const primary = isNumeric ? `${idPath}/${encoded}` : `${slugPath}/${encoded}`;
  const fallback = isNumeric ? `${slugPath}/${encoded}` : `${idPath}/${encoded}`;

  try {
    return await getFn(primary);
  } catch (error) {
    if (error.status === 404 || error.status === 400) {
      return getFn(fallback);
    }
    throw error;
  }
}
